import { Autocomplete } from '@mui/material';
import { FC, memo, SetStateAction, useCallback, useMemo } from 'react';

import { StyledTextField } from 'components/styled';
import { Booking, Chassis, Customer, EquipmentType, Genset, UnitStatus } from 'types';

import { SearchableSelect, SearchableSelectOption } from 'components';
import { useLookups, useSizeTypes } from 'contexts';
import { useNewTransaction } from '..';
import { AccountSearch } from '../account';
import { BookingInput } from '../booking';
import { InboundUnitSearch, OutboundUnitSearch } from '../equipment';
import ManufacturedDate from '../equipment/manufactured-date';

const isNonEmptyChassisStack = (stack: Chassis[] | Genset[]): stack is Chassis[] =>
  !!stack.length && stack[0] instanceof Chassis;

/* memoize change handler to avoid rerendering account field / repeated valiidation calls */
const ChassisAccount: FC<{ index: number }> = memo(({ index }) => {
  const { stack, setStack } = useNewTransaction();
  const indexChassis: Chassis = useMemo(() => stack[index] as Chassis, [stack, index]);
  const setCustomer = useCallback(
    (customer?: Customer | null) => {
      setStack(readOnlyStack => {
        const stackCopy = [...readOnlyStack];
        const [current] = stackCopy.splice(index, 1);
        stackCopy.splice(index, 0, new Chassis({ ...current, customer }));
        return stackCopy as typeof readOnlyStack;
      });
    },
    [index, setStack]
  );
  return (
    <AccountSearch
      required
      unit={indexChassis}
      setCustomer={setCustomer}
      data-cy={`mounted-chassis-${index}-account-search`}
    />
  );
});

const ChassisBooking: FC<{ index: number }> = memo(({ index }) => {
  const { stack, transaction, setStack } = useNewTransaction();
  const indexChassis: Chassis = useMemo(() => stack[index] as Chassis, [stack, index]);
  const setBooking = useCallback(
    (args?: SetStateAction<Partial<Booking> | undefined | null>) => {
      setStack(readOnlyStack => {
        const stackCopy = [...readOnlyStack];
        if (index in stackCopy) {
          stackCopy[index].booking = typeof args === 'function' ? args(stackCopy[index].booking) : args;
        }
        return stackCopy as typeof readOnlyStack;
      });
    },
    [index, setStack]
  );
  return (
    <BookingInput
      unit={indexChassis}
      setBooking={setBooking}
      required={!transaction.inbound}
      data-cy={`mounted-chassis-${index}-booking-search`}
    />
  );
});

const ChassisSection: FC = () => {
  const { chassis, stack, setChassis: setBaseChassis, setStack, transaction } = useNewTransaction();
  const { unitStatusOptions } = useLookups();
  const { chassisSizeTypeEntries } = useSizeTypes();

  const sizeTypeOptions: SearchableSelectOption[] = useMemo(() => {
    return chassisSizeTypeEntries.filter(e => e[1].length === 1).map(e => ({ label: e[0], value: e[1][0] }));
  }, [chassisSizeTypeEntries]);

  const deleteChassis = useCallback(
    (index: number) => {
      setStack(readOnlyStack => {
        const stackCopy = [...readOnlyStack];
        stackCopy.splice(index, 1);
        return stackCopy as typeof readOnlyStack;
      });
    },
    [setStack]
  );

  const setChassis = useCallback(
    (index: number, args?: SetStateAction<Chassis>) => {
      setStack(readOnlyStack => {
        const stackCopy = [...readOnlyStack];
        if (index in stackCopy) {
          stackCopy[index] = new Chassis(typeof args === 'function' ? args(stackCopy[index] as any) : args);
        }
        return stackCopy as typeof readOnlyStack;
      });
    },
    [setStack]
  );

  const validateOutboundStackedChassis = useCallback(
    async (unit: Chassis | null) => {
      if (!unit) {
        return;
      }
      if (unit.baseUnit) {
        if (!chassis.id) {
          setBaseChassis(new Chassis(unit.baseUnit));
        } else if (chassis.id !== unit.baseUnit.id) {
          return `Base chassis mismatch. ${unit.unitNumber} already mounted on ${unit.baseUnit.unitNumber}`;
        }
      }
      if (unit.mountedUnits?.length) {
        return `Invalid setup. ${unit.mountedUnits.length + 1} units already mounted on ${unit.unitNumber}`;
      }
    },
    [chassis, setBaseChassis]
  );

  if (!isNonEmptyChassisStack(stack)) return null;

  return (
    <>
      {stack.map((c, i) => (
        <div key={c.id}>
          <hr className="mb-4" />
          <div className="grid-3">
            <div>
              {transaction.inbound ? (
                <InboundUnitSearch
                  equipmentType={EquipmentType.CHASSIS}
                  required
                  setUnit={args => setChassis(i, args)}
                  unit={c}
                  data-cy={`mounted-chassis-${i}-inbound-unit-number-search`}
                />
              ) : (
                <OutboundUnitSearch
                  customValidation={validateOutboundStackedChassis}
                  required
                  setUnit={args => setChassis(i, args)}
                  unit={c}
                  data-cy={`mounted-chassis-${i}-outbound-unit-number-search`}
                />
              )}
            </div>
            <div>
              <SearchableSelect
                containerClass="text-field"
                inputClass={!c.sizeType ? 'blank' : ''}
                id={`chassis-${c.id}-size-type`}
                label="SIZE TYPE"
                disabled={!transaction.inbound}
                required
                onClear={() => setChassis(i, c => ({ ...c, sizeType: undefined }))}
                onValueSelected={(sizeType: any) => setChassis(i, c => ({ ...c, sizeType }))}
                options={sizeTypeOptions}
                value={c.sizeType ?? ''}
                data-cy={`mounted-chassis-${i}-size-type-input`}
              />
            </div>
            <div>
              <ChassisAccount key={i} index={i} />
            </div>
            <div>
              <Autocomplete<UnitStatus | ''>
                fullWidth
                autoHighlight
                size="small"
                disabled={!transaction.inbound}
                options={['', ...unitStatusOptions]}
                value={c.unitStatus ?? ''}
                onChange={(_, status) => setChassis(i, c => ({ ...c, unitStatus: status || undefined }))}
                getOptionLabel={status => status ?? ''}
                renderInput={params => (
                  <StyledTextField
                    {...params}
                    label="STATUS"
                    required
                    data-cy={`mounted-chassis-${i}-unit-status-input`}
                  />
                )}
                renderOption={(props, status) => (
                  <li {...props} key={status ?? ''}>
                    {status ?? ''}
                  </li>
                )}
              />
            </div>
            <div>
              <StyledTextField
                fullWidth
                label="LICENSE PLATE"
                disabled={!transaction.inbound}
                value={c.licensePlate || ''}
                onChange={e => setChassis(i, c => ({ ...c, licensePlate: e.target.value.toUpperCase() }))}
                data-cy={`mounted-chassis-${i}-license-plate-input`}
              />
            </div>
            <div>
              <ChassisBooking key={i} index={i} />
            </div>
            <ManufacturedDate unit={c} setUnit={u => setChassis(i, u)} />
            <div className="col-start-3 align-self-center justify-self-end">
              <button
                className="btn-close delete"
                type="button"
                onClick={() => deleteChassis(i)}
                data-cy={`mounted-chassis-${i}-delete-button`}
              >
                <span className="material-icons icon-xlarge">delete</span>
              </button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default ChassisSection;
